// initial state
import apiClient from "@/api/apiClient";

const state = {
    isLoggedIn: false,
    user: null,
    loginRedirectRoute: null, // Route the user tried to go to while not logged in, should be redirected after logging in.
}

// getters
const getters = {
    canUserViewPrices: state => {
        return state.user && state.user.roles.includes('ROLE_PORTAL_PRICES_USER');
    },
    isKeyUser: state => {
        return state.user && state.user.roles.includes('ROLE_PORTAL_KEY_USER');
    },
    hasExternalAuth: state => {
        return state.user && state.user.externalAuth;
    },
    canAccessKnowledgeBase: state => {
        return state.user && state.user.accessKnowledgeBase;
    }
}

// actions
const actions = {
    checkMe({commit}) {
        return apiClient.get('/me', {
            skipInterceptor: true, // Don't trigger a redirect
        })
            .then(response => {
                if (response.status === 200) {
                    commit('SET_AUTH_STATE', { isLoggedIn: true, user: response.data });
                    return true;
                } else {
                    return false;
                }
            })
            .catch(() => {
                // If 401 or other error, user is not authenticated
                return false;
            });
    },
    checkUserExists(context, { username }) {
        let formData = new FormData();
        formData.append('username', username);

        return apiClient.post('/check-user', formData)
            .then(response => {
                if (response.status === 200) {
                    return response.data;
                } else {
                    console.error(response.data.error);
                    return false;
                }
            });
    },
    login({commit}, { username, password, _remember_me }) {
        commit('SET_AUTH_STATE', { isLoggedIn: false})
        return apiClient.post('/login', JSON.stringify({ username, password, _remember_me }))
            .then(response => {
                if (response.status === 200) {
                    commit('SET_AUTH_STATE', {isLoggedIn: true, user: response.data })
                    return true;
                } else {
                    console.error(response.data.error);
                    return false;
                }
            })
    },
    forgotPassword(_, { username }) {
        let formData = new FormData();
        formData.append('username', username);
        return apiClient.post('/forgot-password', formData)
            .then(response => {
                if (response.status === 200) {
                    return true;
                } else {
                    console.error(response.data.error);
                    return false;
                }
            })
    },
    resetPassword(_, { token, password }) {
        let formData = new FormData();
        formData.append('token', token);
        formData.append('password', password);
        return apiClient.post('/reset-password', formData)
            .then(response => {
                if (response.status === 200) {
                    return true;
                } else {
                    console.error(response.data.error);
                    return false;
                }
            })
    },
    updatePassword(_, { password, old_password }) {
        let formData = new FormData();
        formData.append('password', password);
        formData.append('old_password', old_password);
        return apiClient.post('/update-password', formData)
            .then(response => {
                if (response.status === 200) {
                    return true;
                } else {
                    console.error(response.data.error);
                    return false;
                }
            })
    },
    changeLanguages({ commit }, { languages }) {
        let formData = new FormData();
        formData.append('languages', JSON.stringify(languages));
        return apiClient.post('/change-language', formData)
            .then(response => {
                if (response.status === 200) {
                    commit('CHANGE_USER_LANGUAGES', languages);
                    return true;
                } else {
                    return false;
                }
            })
    },
    logout({commit}, force = false) {
        if (force) {
            // Log API out
            apiClient.post('/logout')
                .then((response) => {
                    // Log out at external provider
                    if (force && response.data.logoutUrl) {
                        window.location = response.data.logoutUrl;
                        return;
                    }

                    // Use a hard reload of the app
                    window.location.href = '/login';
                });
        }

        commit('CLEAR_AUTH_STATE');
    },
    setLoginRedirectRoute({commit}, route) {
        commit('SET_LOGIN_REDIRECT_ROUTE', route);
    }
}

// mutations
const mutations = {
    SET_AUTH_STATE(state, { isLoggedIn, user }) {
        state.isLoggedIn = isLoggedIn;
        state.user = user;
    },
    SET_LOGIN_REDIRECT_ROUTE(state, route) {
        state.loginRedirectRoute = route;
    },
    CLEAR_AUTH_STATE(state) {
        state.isLoggedIn = null;
        state.user = null;
    },
    CHANGE_USER_LANGUAGES(state, languages) {
        if (state.user) {
            state.user.languages = languages;
        }
    }
}

export default {
    state,
    getters,
    actions,
    mutations
}
